import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthSettings, useGrowthSettingsMutation } from '../../hooks/useGrowthSettings';
import { supabase } from '../../services/supabase';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.25rem;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid var(--outline);
  background: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $primary }) => ($primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  cursor: pointer;
  margin-right: 0.5rem;
  margin-top: 0.5rem;
  &:disabled {
    opacity: 0.6;
  }
`;

const DEFAULT_KEYS = ['home', 'plan_visit', 'kids', 'contact', 'events', 'give', 'sermons'];

export default function GrowthSettingsPage() {
  const navigate = useNavigate();
  const { data: settings, isLoading } = useGrowthSettings();
  const mutate = useGrowthSettingsMutation();

  const [ourUrls, setOurUrls] = useState<Record<string, string>>({});
  const [gbpUrl, setGbpUrl] = useState('');
  const [peersJson, setPeersJson] = useState('[]');

  useEffect(() => {
    if (!settings) return;
    setOurUrls(typeof settings.our_site_urls === 'object' ? { ...settings.our_site_urls } : {});
    setGbpUrl(settings.gbp_maps_url ?? '');
    setPeersJson(JSON.stringify(settings.peers ?? [], null, 2));
  }, [settings]);

  const hasSettings = settings != null;

  const handleSave = async () => {
    let peers: { name: string; site_urls: Record<string, string>; gbp_maps_url?: string }[] = [];
    try {
      peers = JSON.parse(peersJson);
    } catch {
      alert('Invalid JSON for peers');
      return;
    }
    await mutate.mutateAsync({
      our_site_urls: ourUrls,
      gbp_maps_url: gbpUrl.trim() || null,
      peers,
    });
  };

  const handleGeneratePages = async () => {
    if (!hasSettings) return;
    const inserts: { scope: string; peer_name: string | null; label: string; url: string }[] = [];
    for (const [label, url] of Object.entries(ourUrls)) {
      if (url?.trim()) inserts.push({ scope: 'our', peer_name: null, label, url: url.trim() });
    }
    if (gbpUrl?.trim()) {
      inserts.push({ scope: 'gbp', peer_name: null, label: 'GBP', url: gbpUrl.trim() });
    }
    const peers = (() => {
      try {
        return JSON.parse(peersJson) as { name: string; site_urls: Record<string, string>; gbp_maps_url?: string }[];
      } catch {
        return [];
      }
    })();
    for (const p of peers) {
      const urls = p.site_urls ?? {};
      for (const [label, url] of Object.entries(urls)) {
        if (url?.trim()) inserts.push({ scope: 'peer', peer_name: p.name, label, url: url.trim() });
      }
    }
    const { error } = await supabase.from('growth_monitored_pages').insert(inserts);
    if (error) alert(error.message);
    else {
      alert(`Created ${inserts.length} monitored pages. You can edit or disable them in Peers.`);
      navigate('/growth/peers');
    }
  };

  if (isLoading) return <Page><GrowthNav /><p>Loading settings…</p></Page>;

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Growth settings</SectionTitle>
      {!hasSettings && (
        <Card>
          <p>No settings yet. Add your WordPress URLs and peers below, then Save. After saving, use &quot;Generate monitored pages&quot; to create pages for snapshots.</p>
        </Card>
      )}

      <Card>
        <SectionTitle>Our site URLs</SectionTitle>
        {DEFAULT_KEYS.map((k) => (
          <div key={k}>
            <Label>{k}</Label>
            <Input
              type="url"
              placeholder={`https://yoursite.com/${k.replace(/_/g, '-')}`}
              value={ourUrls[k] ?? ''}
              onChange={(e) => setOurUrls((u) => ({ ...u, [k]: e.target.value }))}
            />
          </div>
        ))}
      </Card>

      <Card>
        <Label>Google Business Profile (Maps) URL</Label>
        <Input
          type="url"
          placeholder="https://maps.google.com/..."
          value={gbpUrl}
          onChange={(e) => setGbpUrl(e.target.value)}
        />
      </Card>

      <Card>
        <SectionTitle>Peers (JSON array)</SectionTitle>
        <p style={{ fontSize: '0.85rem', color: 'var(--onSurfaceVariant)', marginBottom: '0.5rem' }}>
          Array of &#123; name, site_urls: &#123; home, plan_visit, events &#125;, gbp_maps_url? &#125;
        </p>
        <textarea
          value={peersJson}
          onChange={(e) => setPeersJson(e.target.value)}
          rows={12}
          style={{ width: '100%', maxWidth: '600px', fontFamily: 'monospace', fontSize: '0.85rem', padding: '0.5rem' }}
        />
      </Card>

      <div>
        <Btn $primary onClick={() => handleSave()} disabled={mutate.isPending}>
          Save settings
        </Btn>
        {hasSettings && (
          <Btn onClick={() => handleGeneratePages()}>
            Generate monitored pages from settings
          </Btn>
        )}
      </div>
    </Page>
  );
}
